import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
// import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded"; // used by the retired hierarchy tile
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import { requestProlongation } from "../actions";
import { currentWorkspaceName } from "../utils/currentWorkspace";
import { viewContextLine, viewName } from "../utils/viewContext";
import { useViewConfig, useViewConfigUnavailable } from "./app/ViewConfigContext";
import { canEditDatabase, showsLibraryName } from "../utils/permissions";
import { SHOW_PLANS } from "../features";
import readFeatures from "../utils/featureFlags";
import businessFlowsTheme, { brand, radius } from "../theme/businessFlowsTheme";
import { Caution, Hint, Toast } from "./workspaces/WorkspaceUi";
import ContextBand, { eyebrowSx } from "./home/ContextBand";
import WaysIn from "./home/WaysIn";
import LibraryPanel from "./home/LibraryPanel";
import "./auth/auth.css";
import useBranding, { mailto } from "../utils/branding";

// Home is the front door of a process library, so it says three things and
// nothing else: where you are, how to get into the work, and what is in the
// library that is open.
//
// It used to be a fixed 2x2 tile grid beside a marketing card with a stock
// illustration, under a strip reading "500+ Reference processes / E2E
// Architecture maps / AI / SAP S/4HANA ready". The grid had holes whenever an
// instance switched a feature off, and the strip was copy printed over every
// customer's database - true of none of them in particular. Both are gone:
// the ways in compose themselves from however many features are on
// (components/home/WaysIn.jsx), and the figures are the library's own
// (components/home/LibraryPanel.jsx), shown only where the server sent them.

// Only ever read behind SHOW_PLANS: with plans out of the customer's UI a
// reader never meets one of these names. Kept so flipping the switch back
// restores the line it used to print.
const PLAN_NAMES = { trial: "Trial", ultimate: "Ultimate", ultimatePlus: "Ultimate Plus", expired: "Expired" };

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const daysUntil = (date) => {
  if (!date) return null;
  const ms = new Date(date) - new Date();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

const Home = (props) => {
  const branding = useBranding();
  const upgradeMail = mailto(branding.supportEmail, `${branding.productName} - request to update my subscription`);
  const history = useHistory();
  const viewConfig = useViewConfig();
  const viewConfigUnavailable = useViewConfigUnavailable();
  const [toast, setToast] = useState(null);
  const user = props.user || {};
  // The database itself can't be reached (App.js asked it where the explorer
  // starts and it answered 503). The ways in stay where they are and the
  // banner below says which database and why, in the server's own words.
  const outage = props.databaseOutage || null;
  const outageDatabase = outage ? user.databaseLabels?.[outage.database] || outage.database : null;

  // Run once on mount: validate session and sync database from localStorage
  useEffect(() => {
    if (!user.id) {
      localStorage.clear();
      history.push("/login");
      return;
    }
    const database = localStorage.getItem("db");
    if (database && database !== user.mainDatabase) {
      // Some routes render Home without one (App.js: /success, /error and the
      // token landing pages). Nothing here may depend on it.
      if (props.setUser) props.setUser({ ...user, mainDatabase: database });
      history.push(`/home/${database}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // What this instance offers (Console → Features).
  const features = readFeatures();

  const navigate = (path) => {
    if (user.mainDatabase) history.push(path);
  };

  const accountExtensionRequest = async () => {
    const res = await requestProlongation(user.email);
    setToast({
      tone: res.success ? "success" : "error",
      text: res.success ? res.msg || "Request sent. We'll email you." : "The request didn't go through. Try again later.",
    });
  };

  const plan = user.plan || user.subscription;
  // The context a reader wants first: which workspace, which library, and the
  // view that library is read through - the three things that decide what
  // they are looking at. The view comes from the configuration the app
  // already loads; where none applies it says so (utils/viewContext.js).
  const workspaceName = currentWorkspaceName(user);
  // The library's name is shown to the people the console opens for; a
  // customer is told the workspace and the view instead, which is what says
  // what they can see (utils/permissions.js showsLibraryName). Empty here
  // takes the fact out of the masthead and leaves "In this library" saying
  // exactly that, rather than naming a key from our own registry.
  const databaseName = user.mainDatabase && showsLibraryName(user) ? user.databaseLabels?.[user.mainDatabase] || user.mainDatabase : "";
  const viewText = viewContextLine(viewConfig?.view);
  // The trial countdown and "Request more time" go with the rest of the plan
  // wording (frontend/src/features.js SHOW_PLANS). Plans decide nothing now,
  // so "N days left in your trial" promised an ending that never comes and
  // offered a request for time nobody was taking away. Flipping SHOW_PLANS
  // back brings all of it back at once.
  //
  // When a workspace gets an explicit "access until" date of its own (see
  // docs/superpowers/plans/2026-09-28-retiring-subscriptions.md), that date -
  // not a plan's trial - is what belongs in this line.
  const isTrial = user.subscription === "trial";
  const trialDays = SHOW_PLANS && isTrial && user.planSource !== "organisation" ? daysUntil(user.expirationDate) : null;
  const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);

  // Ways in, in the order a reader meets the work: the explorer first (it is
  // where a library is read), then search, then the AI services. The first of
  // whatever is left is the banner - see components/home/WaysIn.jsx.
  const ways = [
    // The static hierarchy entry is retired: diagrams open from search and the explorer.
    // features.businessFlows && { key: "bf", icon: <AccountTreeRoundedIcon />, title: "Business Flows", ... }
    //
    // Shown only when the database has somewhere to start - an empty one has
    // no start object, and it would open a broken link. A database that
    // couldn't be asked is not a database without a start object: it stays,
    // and saying so is the banner's job.
    features.relationshipExplorer &&
      (outage || (typeof props.rootGUID === "string" && props.rootGUID)) && {
        key: "re",
        icon: <HubRoundedIcon />,
        title: "Relationship Explorer",
        description: "See how processes, systems and roles connect, and open any diagram from there.",
        onClick: outage
          ? () => setToast({ tone: "warning", text: outage.msg })
          : () => navigate(`${props.path}/object/${props.rootGUID}`),
      },
    features.advanceSearch && {
      key: "as",
      icon: <ManageSearchRoundedIcon />,
      title: "Advanced search",
      description: "Find any object by name and type, and open its diagram.",
      onClick: () => navigate(`${props.path}/advance-search`),
    },
    features.flowos && {
      key: "flowos",
      icon: <AutoAwesomeRoundedIcon />,
      title: "FlowOS AI",
      description: "Analyse processes, trends and debates with AI.",
      onClick: () => navigate("/flowos"),
    },
  ].filter(Boolean);

  // A type in the library opens the search it belongs to. Only offered where
  // that search exists on this instance.
  const openType = features.advanceSearch
    ? (type) => navigate(`${props.path}/advance-search?type=${encodeURIComponent(type.objectType)}`)
    : null;
  const openAll = features.advanceSearch ? () => navigate(`${props.path}/advance-search`) : null;

  const page = {
    width: "100%",
    maxWidth: 1440,
    mx: "auto",
    px: { xs: 2, sm: 3, md: 4 },
    py: { xs: 2.5, md: 3 },
    boxSizing: "border-box",
  };

  return (
    <ThemeProvider theme={businessFlowsTheme}>
      <Box className="bfx" sx={{ flex: 1, minHeight: 0, overflowY: "auto", bgcolor: "background.default" }}>
        <Box sx={page}>
          {outage && (
            <Caution sx={{ mb: 2 }} role="alert">
              {outageDatabase ? `${outageDatabase}: ` : ""}
              {outage.msg}
            </Caution>
          )}

          {isSafari && (
            <Hint sx={{ mb: 2 }}>
              For the best experience with large process diagrams, use Google Chrome or Mozilla Firefox.
            </Hint>
          )}

          {/* Masthead: the greeting, then the three facts that decide what
              this reader is looking at. Type carries the hierarchy - no box
              around any of it. */}
          <Box component="header" sx={{ pb: { xs: 2.5, md: 3 }, borderBottom: `1px solid ${brand.line}` }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontSize: "clamp(1.55rem, 1.1rem + 2vw, 2.35rem)", lineHeight: 1.1 }}
            >
              {greeting()}
              {user.firstName ? `, ${user.firstName}` : ""}
            </Typography>

            <ContextBand
              workspaceName={workspaceName}
              databaseName={databaseName}
              viewText={user.mainDatabase ? viewText : ""}
              viewNamed={Boolean(viewName(viewConfig?.view))}
            />

            {/* The plan is billing, not context, and so is the trial that
                came with it: both are behind SHOW_PLANS
                (frontend/src/features.js). */}
            {(trialDays !== null || (SHOW_PLANS && plan)) && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 0.5 }}>
                {SHOW_PLANS && plan && (
                  <Typography variant="caption" color="text.secondary">
                    {PLAN_NAMES[plan] || plan} plan
                  </Typography>
                )}
                {trialDays !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {trialDays === 1 ? "1 day" : `${trialDays} days`} left in your trial.
                  </Typography>
                )}
                {trialDays !== null && (
                  <Button size="small" onClick={accountExtensionRequest} sx={{ px: 1 }}>
                    Request more time
                  </Button>
                )}
                {SHOW_PLANS && plan !== "ultimatePlus" && (
                  <Button size="small" href={upgradeMail} sx={{ px: 1 }}>
                    Upgrade plan
                  </Button>
                )}
              </Stack>
            )}
          </Box>

          {/* Signed in with nothing to open. App.js normally catches this
              with NoDatabaseCard, but a few routes render Home directly, and
              a page that says what to do beats an empty grid. */}
          {!user.mainDatabase ? (
            <Box
              sx={{
                mt: { xs: 2.5, md: 3 },
                p: { xs: 2.5, md: 3 },
                maxWidth: 640,
                borderRadius: radius.surface,
                bgcolor: "#fff",
                border: `1px solid ${brand.line}`,
              }}
            >
              <Box
                aria-hidden="true"
                sx={{ width: 44, height: 44, borderRadius: radius.card, display: "grid", placeItems: "center", bgcolor: brand.mark, color: brand.ink, mb: 1.5 }}
              >
                <StorageRoundedIcon />
              </Box>
              <Typography component="h2" sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
                No library is open
              </Typography>
              <Typography variant="body2" sx={{ color: brand.prose, mt: 0.75, maxWidth: "52ch" }}>
                {workspaceName
                  ? `${workspaceName} has no process library connected to your account yet.`
                  : "Your account has no process library connected yet."}{" "}
                Ask {branding.companyName || "bpExperts"} to connect one, and everything on this page fills itself in.
              </Typography>
              <Button
                variant="contained"
                href={mailto(branding.supportEmail, `${branding.productName} - I have no library to open`)}
                sx={{ mt: 2 }}
              >
                Ask for a library
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                mt: { xs: 2.5, md: 3 },
                display: "grid",
                // Two columns only where there is room for both to be read.
                // Below that they stack, ways in first: it is what a reader
                // came to press.
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.15fr) minmax(0, 0.85fr)" },
                gap: { xs: 2.5, md: 3 },
                alignItems: "start",
              }}
            >
              <WaysIn items={ways} />
              <LibraryPanel
                config={viewConfig}
                unavailable={viewConfigUnavailable || outage}
                databaseName={databaseName}
                onOpenType={openType}
                onOpenAll={openAll}
                canImport={canEditDatabase(user, user.mainDatabase)}
              />
            </Box>
          )}

          {/* The brand, as one line rather than a card with a stock
              illustration: it belongs on this page, it does not belong in
              half of it. */}
          <Typography
            component="p"
            sx={{ ...eyebrowSx, mt: { xs: 3, md: 4 }, textTransform: "none", letterSpacing: 0, fontSize: "0.82rem", fontWeight: 400 }}
          >
            {branding.productName} is built by{" "}
            <Box
              component="a"
              href={branding.companyUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: brand.ink,
                fontWeight: 650,
                textDecoration: "underline",
                textDecorationColor: brand.signal,
                textDecorationThickness: 3,
                textUnderlineOffset: 3,
              }}
            >
              {branding.companyName || "bpExperts"}
            </Box>
            , the business integrator.
          </Typography>
        </Box>

        <Toast notice={toast} onClose={() => setToast(null)} />
      </Box>
    </ThemeProvider>
  );
};

export default Home;
